import React, { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { AppButton, Callout, FileUpload, FormField, Input, NativeSelect, Textarea } from "../ui";
import { adminSearchResources, createSessionAssignment } from "../../utils/smartLearning";
import { ACCEPT, ACCEPTED_HINT, MAX_FILE_MB, MAX_FILES, fileProblem } from "../Assignments/general/assignmentFiles";
import { maxMarksProblem, parseNumber } from "../Assignments/general/assignmentRules";

/**
 * Create an assignment (or edit one) and hand it back to be attached.
 *
 * An assignment belongs to ONE batch — its students are the ones who see it —
 * so a batch is required. Inside a batch's own screens it is fixed to that batch.
 *
 * Props:
 *   courseId, batchId — batchId preselects (and the list is the course's batches)
 *   initial           — edit mode: { batchId, title, dueDate: 'YYYY-MM-DD', maxMarks, description }
 *   submitFn          — (FormData) => saved assignment; defaults to creating one
 *   submitLabel
 *   onCreated(resource), onCancel
 */
const AssignmentForm = ({ courseId, batchId, initial, submitFn, submitLabel = "Create and attach", onCreated, onCancel }) => {
  const isEdit = !!initial;
  const [batches, setBatches] = useState([]);
  const [form, setForm] = useState({
    batchId: initial?.batchId || batchId || "",
    title: initial?.title || "",
    dueDate: initial?.dueDate || "",
    maxMarks: initial?.maxMarks ?? 10,
    description: initial?.description || "",
  });
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [failure, setFailure] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    adminSearchResources({ kind: "assignment", courseId })
      .then(({ facets }) => { if (alive) setBatches(facets?.batches || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [courseId]);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: "" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const next = {};
    if (!form.batchId) next.batchId = "Choose the batch whose students do it.";
    if (!form.title.trim()) next.title = "Give the assignment a title.";
    if (!form.dueDate) next.dueDate = "Set a due date.";
    if (!isEdit || Number(initial?.maxMarks) !== parseNumber(form.maxMarks)) {
      const marksError = maxMarksProblem(parseNumber(form.maxMarks));
      if (marksError) next.maxMarks = marksError;
    }
    setErrors(next);
    if (Object.values(next).some(Boolean)) return;

    setSaving(true);
    setFailure("");
    try {
      const fd = new FormData();
      fd.append("title", form.title.trim());
      fd.append("batchId", form.batchId);
      fd.append("dueDate", new Date(form.dueDate).toISOString());
      fd.append("maxMarks", String(Number(form.maxMarks)));
      if (form.description.trim()) fd.append("description", form.description.trim());
      files.forEach((f) => fd.append("attachments", f));
      const a = submitFn ? await submitFn(fd) : await createSessionAssignment(fd);
      if (!a?._id) throw new Error("The save did not return an assignment.");
      onCreated({ _id: a._id, title: a.title, kind: "assignment" });
    } catch (err) {
      setFailure(err?.response?.data?.message || err.message || "The assignment was not saved. Try again.");
      setSaving(false);
    }
  };

  const batchLocked = !!batchId && !isEdit;

  return (
    <form onSubmit={submit} noValidate className="tw-flex tw-flex-col tw-gap-4">
      {failure && <Callout tone="danger" role="alert">{failure}</Callout>}
      <FormField label="Batch" required error={errors.batchId} hint="Its students see the assignment.">
        <NativeSelect value={form.batchId} onChange={(e) => set("batchId", e.target.value)} disabled={saving || batchLocked}>
          <option value="">Choose a batch…</option>
          {batches.map((b) => <option key={b._id} value={b._id}>{b.name}</option>)}
          {batchLocked && !batches.some((b) => String(b._id) === String(batchId)) && <option value={batchId}>This batch</option>}
        </NativeSelect>
      </FormField>
      <FormField label="Title" required error={errors.title}>
        <Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Build a to-do app" disabled={saving} maxLength={200} />
      </FormField>
      <div className="tw-grid tw-gap-4 min-[576px]:tw-grid-cols-2">
        <FormField label="Due date" required error={errors.dueDate}>
          <Input type="date" value={form.dueDate} onChange={(e) => set("dueDate", e.target.value)} disabled={saving} />
        </FormField>
        <FormField label="Out of (marks)" required error={errors.maxMarks}>
          <Input type="number" min={1} step="any" value={form.maxMarks} onChange={(e) => set("maxMarks", e.target.value)} disabled={saving} />
        </FormField>
      </div>
      <FormField label="Instructions" hint="What students need to do and hand in.">
        <Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} disabled={saving} />
      </FormField>
      <FileUpload
        files={files}
        onChange={setFiles}
        multiple
        maxFiles={MAX_FILES}
        maxSizeMB={MAX_FILE_MB}
        accept={ACCEPT}
        validate={fileProblem}
        typesHint={ACCEPTED_HINT}
        label={isEdit ? "Add attachments (optional)" : "Attachments (optional)"}
        hint={isEdit ? `Attachments already on it are kept. Up to ${MAX_FILES} in all.` : `Up to ${MAX_FILES} files`}
        disabled={saving}
      />
      <div className="tw-flex tw-flex-wrap tw-justify-end tw-gap-2">
        <AppButton type="button" variant="outline" onClick={onCancel} disabled={saving}>Cancel</AppButton>
        <AppButton type="submit" disabled={saving}>
          <Plus size={14} aria-hidden="true" /> {saving ? "Saving…" : submitLabel}
        </AppButton>
      </div>
    </form>
  );
};

export default AssignmentForm;
